import React, { ReactNode } from "react"
import { Box, makeStyles, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faBell } from "@fortawesome/pro-light-svg-icons/faBell"
import { faTag } from "@fortawesome/pro-light-svg-icons/faTag"
import { lightBlue } from "@material-ui/core/colors"
import { LoadingSwitch } from "shared/Components/Loading/LoadingSwitch"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { PushNotificationTypes } from "../usePushPermission"

const useStyles = makeStyles((theme) => ({
    list: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
    },
    card: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[200]}`,
    },
    iconCircle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: theme.spacing(5),
        height: theme.spacing(5),
        borderRadius: "50%",
        backgroundColor: lightBlue[50],
        color: theme.palette.primary.main,
    },
    text: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        fontWeight: "bold",
    },
}))

type PushTypeCardProps = Readonly<{
    icon: IconProp
    title: ReactNode
    description: ReactNode
    checked: boolean
    loading: boolean
    disabled: boolean
    onChange: (checked: boolean) => void
}>

function PushTypeCard({ icon, title, description, checked, loading, disabled, onChange }: PushTypeCardProps) {
    const classes = useStyles()

    return (
        <Box className={classes.card}>
            <Box className={classes.iconCircle}>
                <FontAwesomeIcon icon={icon} />
            </Box>
            <Box className={classes.text}>
                <Typography variant="body1" className={classes.title}>{title}</Typography>
                <Typography variant="body2" color="textSecondary">{description}</Typography>
            </Box>
            <LoadingSwitch
                checked={checked}
                loading={loading}
                disabled={disabled}
                color="primary"
                onChange={(_event, value) => onChange(value)}
            />
        </Box>
    )
}

type PushTypeTogglesProps = Readonly<{
    value: PushNotificationTypes
    onChange: (type: keyof PushNotificationTypes, checked: boolean) => void
    // Which toggle is currently being saved (if any)
    saving?: keyof PushNotificationTypes
    disabled?: boolean
    className?: string
}>

/**
 * The two kinds of push notifications the user can opt in to: operational (orders, bookings) and
 * marketing (news, offers). Shared by the onboarding step and the settings page.
 */
export function PushTypeToggles({ value, onChange, saving, disabled = false, className }: PushTypeTogglesProps) {
    const classes = useStyles()

    return (
        <Box className={className}>
            <Box className={classes.list}>
                <PushTypeCard
                    icon={faBell}
                    title={<LocalizedStrict id="push-type-tx-title"><span>Drift og ordrer</span></LocalizedStrict>}
                    description={<LocalizedStrict id="push-type-tx-description"><span>Vigtige beskeder, ordrer og møder.</span></LocalizedStrict>}
                    checked={value.forTx}
                    loading={saving === "forTx"}
                    disabled={disabled}
                    onChange={(checked) => onChange("forTx", checked)}
                />
                <PushTypeCard
                    icon={faTag}
                    title={<LocalizedStrict id="push-type-news-title"><span>Nyheder og tilbud</span></LocalizedStrict>}
                    description={<LocalizedStrict id="push-type-news-description"><span>Dagens menu, nyheder og tilbud.</span></LocalizedStrict>}
                    checked={value.forNews}
                    loading={saving === "forNews"}
                    disabled={disabled}
                    onChange={(checked) => onChange("forNews", checked)}
                />
            </Box>
        </Box>
    )
}
